import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";
import Field from "components/Field";
import RedirectBackButton from "components/RedirectBackButton";
import SelectTimeZone from "components/SelectTimeZone";
import { Decorator } from "final-form";
import createDecorator from "final-form-calculate";
import getUserDisplayName from "helpers/userHelper";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { useMemo } from "react";
import { FormProps, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { OrganizationUserDetails } from "services/KnowfullyAdminClient";
import { createFieldResetter, globalContextUpdaters } from "utils/formUtils";
import ApplicationUsers from "./ApplicationUsers";
import RoleField from "./RolesField";
import UserCredentialList from "./UserCredentials/UserCredentialList";

const useStyles = (theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
  },
  chip: {
    margin: 2,
  },
});

const { Form } = withTypes<OrganizationUserDetails>();

type UserFormProps = {
  user?: OrganizationUserDetails;
  submitLabel: string;
  onSubmit: (values: OrganizationUserDetails) => void;
  onCancel?: () => void;
};

const UserForm = ({
  user,
  submitLabel,
  onCancel,
  onSubmit,
}: UserFormProps & FormProps<OrganizationUserDetails>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { userDetailsUrl } = useGlobalContext();
  const isEdit = !!user;

  const title = isEdit ? getUserDisplayName(user, "") : t({ id: "newUser" });
  useTitle(title);

  const resetter = (accessor: keyof OrganizationUserDetails, resetValue: any) =>
    createFieldResetter(accessor, resetValue);

  const calculator = useMemo(() => {
    const updates = {
      ...resetter("roles", []),
    };

    return createDecorator(
      ...globalContextUpdaters(isEdit ? {} : updates)
    ) as Decorator<OrganizationUserDetails>;
  }, [isEdit]);

  return (
    <>
      {!!user && (
        <Box mb={3}>
          <RedirectBackButton
            redirectRoute={userDetailsUrl(user.id)}
            labelKey="backToUser"
          />
        </Box>
      )}

      <Form
        onSubmit={onSubmit}
        initialValues={user}
        decorators={[calculator]}
        validate={(values) => {
          const errors = {} as any;
          if (!values.organizationId) {
            errors.organizationId = t({ id: "requiredLabel" });
          }
          return errors;
        }}
        render={({ handleSubmit, submitting, submitError, values, form }) => (
          <form onSubmit={handleSubmit} noValidate>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <ContextOrganizationField
                  values={values}
                  mode={
                    isEdit
                      ? ContextOrganizationFieldMode.EditDisabled
                      : ContextOrganizationFieldMode.SetByHeader
                  }
                  formChange={form.change}
                />

                <Field name="firstName">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "firstNameLabel" })}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="lastName">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "lastNameLabel" })}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="email" required isEmail>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        type="email"
                        label={t({ id: "emailLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="timeZone">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectTimeZone
                          label={t({ id: "timeZoneLabel" })}
                          {...input}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Typography sx={styles.subtitle} variant="h6">
                  <T id="userFormExternalTitle" />
                </Typography>

                <Field name="externalSourceId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "externalSourceId" })}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Typography sx={styles.subtitle} variant="h6">
                  <T id="rolesLabel" />
                </Typography>

                <Field name="roles">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <RoleField {...input} />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              {!isEdit && (
                <Button disabled={submitting} onClick={onCancel}>
                  {t({ id: "cancelButton" })}
                </Button>
              )}

              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                startIcon={submitting && <CircularProgress size={16} />}
              >
                {submitLabel}
              </Button>
            </DialogActions>
          </form>
        )}
      />

      {!!user && (
        <>
          <h2>
            <T id="propertiesLabel" />
          </h2>
          <Accordion key={t({ id: "applicationsTitle" })}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t({ id: "applicationsTitle" })}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ApplicationUsers userId={user.id} />
            </AccordionDetails>
          </Accordion>

          <UserCredentialList userId={user.id} />
        </>
      )}
    </>
  );
};

export default UserForm;
